import { z } from 'zod';
import { ProgressPut, InviteBody, InviteResult, INVITE_COHORT_MAX } from '@/shared/contracts';

export const PRIVATE_PREFIX = 'ea-private:';
const OWNER = `${PRIVATE_PREFIX}owner`;
export const AssessmentDraft = z.object({
  attempt_id: z.string(),
  answers: z.record(z.string(), z.number().int().min(0).max(3)),
  submissionKey: z.string(),
  submitting: z.boolean(),
});
export type AssessmentDraft = z.infer<typeof AssessmentDraft>;
export const InviteDraft = z.object({
  emails: z.array(InviteBody.shape.emails.element).max(INVITE_COHORT_MAX),
  role: InviteBody.shape.role,
  result: InviteResult,
  key: z.uuid(),
  keyCreatedAt: z.number().int().nonnegative(),
  pending: z.boolean(),
});
export type InviteDraft = z.infer<typeof InviteDraft>;

function readStored<T>(key: string, schema: z.ZodType<T>): T | null {
  try {
    const parsed = schema.safeParse(JSON.parse(localStorage.getItem(key) ?? 'null'));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
export const readDraft = (key: string) => readStored(key, AssessmentDraft);
export const readProgressDraft = (key: string) => readStored(key, ProgressPut);
export const readInviteDraft = (key: string) => readStored(key, InviteDraft);
export function writeDraft(
  key: string,
  draft: AssessmentDraft | InviteDraft | z.infer<typeof ProgressPut> | null,
): boolean {
  try {
    if (draft) {
      const owner = localStorage.getItem(OWNER);
      // An in-flight callback from an old session must never repopulate data after sign-out.
      if (!owner || !key.startsWith(`${PRIVATE_PREFIX}${owner}:`)) return false;
      localStorage.setItem(key, JSON.stringify(draft));
    } else localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}
export function clearPrivateStorage(): void {
  try {
    for (const key of Object.keys(localStorage))
      if (key.startsWith(PRIVATE_PREFIX)) localStorage.removeItem(key);
  } catch {
    /* Unavailable storage cannot retain a new draft. */
  }
}
export function retainPrivateOwner(owner: string | null): void {
  try {
    if (localStorage.getItem(OWNER) !== owner) clearPrivateStorage();
    if (owner) localStorage.setItem(OWNER, owner);
  } catch {
    /* The assessment reports when saving is unavailable. */
  }
}
/** Retire caches made by the old offline implementation, including its cached session. */
export async function purgeLegacyCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    await Promise.all(
      (await caches.keys())
        .filter((name) => name.startsWith('ea-') && !name.startsWith('ea-static-'))
        .map((name) => caches.delete(name)),
    );
  } catch {
    /* The service worker also purges these on activation. */
  }
}
